import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Chip, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Collapse, IconButton, Switch, FormControlLabel, ListSubheader } from '@material-ui/core';
import { getCollections, getCollectionFields } from '../api/actions/query';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left"
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left"
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const SchemaNode = ({ name, type, path, selectedPaths, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const isObject = typeof type === 'object' && type !== null && !Array.isArray(type);

  return (
    <Box>
      <Box display="flex" alignItems="center" py={0.25} borderBottom="1px solid #f9f9f9">
        {isObject ? (
          <IconButton size="small" onClick={() => setExpanded(!expanded)} style={{ padding: 4, marginRight: 4 }}>
            <span style={{ fontSize: '1.2rem', lineHeight: 1, fontWeight: 'bold', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {expanded ? '-' : '+'}
            </span>
          </IconButton>
        ) : (
          <Box width={24} />
        )}

        {!isObject && (
          <Checkbox
            size="small"
            checked={selectedPaths.includes(path)}
            onChange={(e) => onToggle(path, e.target.checked)}
            style={{ padding: 4 }}
          />
        )}
        <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
          {name}
          {!isObject && (
            <Chip size="small" label={type} style={{ height: 18, fontSize: '0.65rem', marginLeft: 8, backgroundColor: '#e0e0e0' }} />
          )}
        </Typography>
      </Box>

      {isObject && (
        <Collapse in={expanded}>
          <Box pl={3} borderLeft="1px solid #e0e0e0" ml={1.5}>
            {Object.entries(type).map(([childName, childType]) => (
              <SchemaNode
                key={childName}
                name={childName}
                type={childType}
                path={`${path}.${childName}`}
                selectedPaths={selectedPaths}
                onToggle={onToggle}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const DynamicFiltersConfig = ({ categoryFields = [], onCategoryUpdate }) => {
  const [collections, setCollections] = useState([]);
  const [fieldsList, setFieldsList] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [fieldSearch, setFieldSearch] = useState('');

  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [newCollectionInput, setNewCollectionInput] = useState('');

  // Extract allowedExternalCollections from categoryFields
  const allowedExternalField = categoryFields.find(f => f.key === 'allowedExternalCollections');
  const allowedExternalCollections = Array.isArray(allowedExternalField?.value) ? allowedExternalField.value : [];

  const handleAddExternalCollection = () => {
    const raw = newCollectionInput.trim();
    if (!raw) return;

    const newItems = raw.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    const updated = [...allowedExternalCollections];

    newItems.forEach(item => {
      if (!updated.some(u => u.toLowerCase() === item.toLowerCase())) {
        updated.push(item);
      }
      if (!collections.some(c => c.toLowerCase() === item.toLowerCase())) {
        setCollections(prev => [...prev, item]);
      }
    });

    const newCategoryFields = [...categoryFields];
    const index = newCategoryFields.findIndex(f => f.key === 'allowedExternalCollections');
    if (index >= 0) {
      newCategoryFields[index] = { ...newCategoryFields[index], value: updated, type: 'array', isStatic: true };
    } else {
      newCategoryFields.push({ key: 'allowedExternalCollections', value: updated, type: 'array', isStatic: true });
    }

    if (onCategoryUpdate) {
      onCategoryUpdate(newCategoryFields);
    }
    if (newItems.length > 0 && !selectedCollection) {
      setSelectedCollection(newItems[0]);
    }
    setNewCollectionInput('');
  };

  const handleRemoveExternalCollection = (indexToRemove) => {
    const updated = allowedExternalCollections.filter((_, idx) => idx !== indexToRemove);
    const newCategoryFields = [...categoryFields];
    const index = newCategoryFields.findIndex(f => f.key === 'allowedExternalCollections');
    if (index >= 0) {
      newCategoryFields[index] = { ...newCategoryFields[index], value: updated, type: 'array', isStatic: true };
    } else {
      newCategoryFields.push({ key: 'allowedExternalCollections', value: updated, type: 'array', isStatic: true });
    }

    if (onCategoryUpdate) {
      onCategoryUpdate(newCategoryFields);
    }
  };
  const [editingField, setEditingField] = useState({ collection: '', fieldObj: null });
  const [customOptionsText, setCustomOptionsText] = useState('');
  const [quickFilters, setQuickFilters] = useState([]);
  const [newQuickFilter, setNewQuickFilter] = useState({ label: '', operator: 'equals', value: '' });

  const [loadingNestedSchema, setLoadingNestedSchema] = useState(false);
  const [flattenedSchema, setFlattenedSchema] = useState([]);
  const [rawNestedSchema, setRawNestedSchema] = useState(null);
  const [selectedNestedPaths, setSelectedNestedPaths] = useState([]);

  const flattenSchemaObj = (schema, parentPath = '') => {
    let result = [];
    Object.entries(schema).forEach(([key, typeRaw]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      if (typeof typeRaw === 'object' && typeRaw !== null) {
        result = [...result, ...flattenSchemaObj(typeRaw, currentPath)];
      } else {
        let type = 'string';
        const lowerType = String(typeRaw).toLowerCase();
        if (lowerType === 'number') type = 'number';
        else if (lowerType === 'boolean') type = 'boolean';
        else if (lowerType === 'date') type = 'date';
        else if (lowerType === 'array') type = 'array';

        result.push({
          path: currentPath,
          type,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()
        });
      }
    });
    return result;
  };

  const handleFetchNestedSchema = async () => {
    if (!editingField.collection || !editingField.fieldObj) return;
    const fieldName = typeof editingField.fieldObj === 'object' ? editingField.fieldObj.name || editingField.fieldObj.field : editingField.fieldObj;

    setLoadingNestedSchema(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';
      const res = await fetch(`${baseUrl}/api/campaign/fields/nested?collection=${editingField.collection}&field=${fieldName}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`
        }
      });
      const data = await res.json();
      if (data.success && data.data) {
        const flat = flattenSchemaObj(data.data, fieldName);
        setFlattenedSchema(flat);
        setRawNestedSchema(data.data);
      } else {
        setFlattenedSchema([]);
        setRawNestedSchema(null);
      }
    } catch (err) {
      console.error("Failed to fetch nested schema", err);
    }
    setLoadingNestedSchema(false);
  };

  const buildNestedStructure = (paths, flatSchema, parentPath) => {
    // This function will convert the selected flat paths back into a recursive ContactField array
    // e.g. ["extensions.address.city", "extensions.address.zip"] => [{id: "extensions.address", type: "object", fields: [{id: "extensions.address.city", ...}]}]
    const result = [];
    const childrenMap = {}; // key: immediate child name, value: array of paths starting with that child

    paths.forEach(p => {
      const relativePath = p.substring(parentPath.length + 1); // remove "extensions."
      const parts = relativePath.split('.');
      const childName = parts[0];
      if (!childrenMap[childName]) childrenMap[childName] = [];
      childrenMap[childName].push(p);
    });

    Object.keys(childrenMap).forEach(childName => {
      const childFullPath = `${parentPath}.${childName}`;
      const childPaths = childrenMap[childName];
      const flatField = flatSchema.find(f => f.path === childFullPath);

      if (flatField) {
        // It's a leaf node
        result.push({
          id: childFullPath,
          label: flatField.label,
          type: flatField.type
        });
      } else {
        // It's an intermediate object node
        result.push({
          id: childFullPath,
          label: childName.charAt(0).toUpperCase() + childName.slice(1).replace(/([A-Z])/g, ' $1').trim(),
          type: 'object',
          fields: buildNestedStructure(childPaths, flatSchema, childFullPath)
        });
      }
    });
    return result;
  };

  const getOperatorsForCurrentField = () => {
    if (!editingField.fieldObj) return [];

    const hasOptions = customOptionsText && customOptionsText.trim().length > 0;
    if (hasOptions) {
      return [
        { value: 'is', label: 'Is' },
        { value: 'isNot', label: 'Is Not' },
        { value: 'in', label: 'In' },
        { value: 'notIn', label: 'Not In' },
        { value: 'isEmpty', label: 'Is Empty' },
        { value: 'isNotEmpty', label: 'Is Not Empty' }
      ];
    }

    let fieldType = editingField.fieldObj.type || 'string';
    let fieldName = String(editingField.fieldObj.name || editingField.fieldObj.field || '').toLowerCase();

    if (newQuickFilter.nestedField && flattenedSchema) {
      const flatField = flattenedSchema.find(f => f.path === newQuickFilter.nestedField);
      if (flatField) {
        fieldType = flatField.type;
        fieldName = newQuickFilter.nestedField.toLowerCase();
      }
    }

    const rawLower = String(fieldType).toLowerCase();

    if (rawLower === 'boolean' || rawLower === 'bool') {
      return [{ value: 'is', label: 'Is' }, { value: 'isNot', label: 'Is Not' }];
    } else if (rawLower === 'number' || rawLower === 'int' || rawLower === 'float') {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not Equals' },
        { value: 'greaterThan', label: 'Greater Than (>)' },
        { value: 'greaterThanOrEqual', label: 'Greater Than or Equal (≥)' },
        { value: 'lessThan', label: 'Less Than (<)' },
        { value: 'lessThanOrEqual', label: 'Less Than or Equal (≤)' },
        { value: 'between', label: 'Between' },
        { value: 'isEmpty', label: 'Is Empty' },
        { value: 'isNotEmpty', label: 'Is Not Empty' }
      ];
    } else if (rawLower.includes('date') || rawLower.includes('time') || fieldName.includes('date') || fieldName.includes('time') || fieldName.includes('slot')) {
      return [
        { value: 'on', label: 'On' },
        { value: 'before', label: 'Before' },
        { value: 'after', label: 'After' },
        { value: 'between', label: 'Between' },
        { value: 'isEmpty', label: 'Is Empty' },
        { value: 'isNotEmpty', label: 'Is Not Empty' }
      ];
    } else if (rawLower === 'array' || rawLower === 'multiselect') {
      return [
        { value: 'hasAny', label: 'Has Any' },
        { value: 'hasAll', label: 'Has All' },
        { value: 'hasNone', label: 'Has None' },
        { value: 'isEmpty', label: 'Is Empty' },
        { value: 'isNotEmpty', label: 'Is Not Empty' }
      ];
    }

    return [
      { value: 'equals', label: 'Equals' },
      { value: 'notEquals', label: 'Not Equals' },
      { value: 'contains', label: 'Contains' },
      { value: 'notContains', label: 'Does Not Contain' },
      { value: 'startsWith', label: 'Starts With' },
      { value: 'endsWith', label: 'Ends With' },
      { value: 'isEmpty', label: 'Is Empty' },
      { value: 'isNotEmpty', label: 'Is Not Empty' }
    ];
  };

  const handleChipClick = (collectionKey, fieldObj) => {
    const isObj = typeof fieldObj === 'object';
    const currentOptions = isObj && Array.isArray(fieldObj.options) ? fieldObj.options.join(', ') : (isObj && Array.isArray(fieldObj.enum) ? fieldObj.enum.join(', ') : '');
    setEditingField({ collection: collectionKey, fieldObj });
    setCustomOptionsText(currentOptions);
    setQuickFilters(isObj && Array.isArray(fieldObj.quickFilters) ? [...fieldObj.quickFilters] : []);

    // Reset the new quick filter with a valid default operator for this field

    // Extract existing nested fields to select in the checklist
    let existingSelectedPaths = [];
    const extractPaths = (fieldsArr) => {
      if (!fieldsArr || !Array.isArray(fieldsArr)) return;
      fieldsArr.forEach(f => {
        if (f.type === 'object' && f.fields) {
          extractPaths(f.fields);
        } else {
          existingSelectedPaths.push(f.id);
        }
      });
    };

    const reconstructSchemaFromFields = (fieldsArr) => {
      let schemaObj = {};
      if (!fieldsArr || !Array.isArray(fieldsArr)) return schemaObj;

      fieldsArr.forEach(f => {
        const childName = f.id.split('.').pop();
        if (f.type === 'object' && f.fields) {
          schemaObj[childName] = reconstructSchemaFromFields(f.fields);
        } else {
          schemaObj[childName] = f.type;
        }
      });
      return schemaObj;
    };

    if (isObj && fieldObj.fields) {
      extractPaths(fieldObj.fields);
      const rootName = typeof fieldObj === 'object' ? fieldObj.name || fieldObj.field : fieldObj;
      const reconstructedSchema = reconstructSchemaFromFields(fieldObj.fields);
      setRawNestedSchema(reconstructedSchema);
      setFlattenedSchema(flattenSchemaObj(reconstructedSchema, rootName));
    } else {
      setRawNestedSchema(null);
      setFlattenedSchema([]);
    }
    setSelectedNestedPaths(existingSelectedPaths);

    setTimeout(() => {
      const tempEditingField = { collection: collectionKey, fieldObj };
      const hasOpt = currentOptions && currentOptions.trim().length > 0;
      let defaultOp = 'equals';
      if (hasOpt) {
        defaultOp = 'is';
      } else {
        const rawLower = String(fieldObj.type || 'string').toLowerCase();
        if (rawLower === 'boolean' || rawLower === 'bool') defaultOp = 'is';
        else if (rawLower.includes('date') || rawLower.includes('time') || String(fieldObj.name || fieldObj.field || '').toLowerCase().includes('date')) defaultOp = 'on';
        else if (rawLower === 'array' || rawLower === 'multiselect') defaultOp = 'hasAny';
      }
      setNewQuickFilter({ label: '', operator: defaultOp, value: '', nestedField: '' });
    }, 0);

    setOptionsDialogOpen(true);
  };

  const handleSaveOptions = () => {
    const { collection, fieldObj } = editingField;
    const fieldName = typeof fieldObj === 'object' ? fieldObj.name || fieldObj.field : fieldObj;
    const optionsArray = customOptionsText.split(',').map(s => s.trim()).filter(Boolean);

    const newCategoryFields = [...categoryFields];
    const collectionIndex = newCategoryFields.findIndex(f => f.key === collection);

    if (collectionIndex >= 0) {
      const fieldIndex = newCategoryFields[collectionIndex].value.findIndex(f =>
        (typeof f === 'object' ? f.name || f.field : f) === fieldName
      );

      if (fieldIndex >= 0) {
        const existingField = newCategoryFields[collectionIndex].value[fieldIndex];
        const updatedField = typeof existingField === 'object' ? { ...existingField } : { name: existingField, type: 'string' };

        if (optionsArray.length > 0) {
          updatedField.options = optionsArray;
        } else {
          delete updatedField.options;
        }

        if (quickFilters.length > 0) {
          updatedField.quickFilters = quickFilters;
        } else {
          delete updatedField.quickFilters;
        }

        if (selectedNestedPaths.length > 0) {
          const nestedFieldsArr = buildNestedStructure(selectedNestedPaths, flattenedSchema, fieldName);
          updatedField.fields = nestedFieldsArr;
        } else {
          delete updatedField.fields;
        }

        newCategoryFields[collectionIndex].value[fieldIndex] = updatedField;

        if (onCategoryUpdate) {
          onCategoryUpdate(newCategoryFields);
        }
      }
    }
    setOptionsDialogOpen(false);
  };

  const currentConfig = categoryFields.find(f => f.key === selectedCollection);
  const selectedFields = currentConfig && Array.isArray(currentConfig.value)
    ? currentConfig.value.map(f => typeof f === 'object' ? f.name || f.field : f)
    : [];

  useEffect(() => {
    const fetchCollections = async () => {
      setLoadingCollections(true);
      try {
        const response = await getCollections();
        if (response?.data?.success) {
          const fetched = response.data.data || [];
          const allowed = Array.isArray(allowedExternalField?.value) ? allowedExternalField.value : [];
          const merged = [...fetched];
          allowed.forEach(col => {
            if (!merged.some(m => m.toLowerCase() === col.toLowerCase())) {
              merged.push(col);
            }
          });
          setCollections(merged);
        }
      } catch (error) {
        console.error("Failed to fetch collections", error);
      }
      setLoadingCollections(false);
    };
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      const fetchFields = async () => {
        setLoadingFields(true);
        try {
          const response = await getCollectionFields(selectedCollection);
          let fields = response?.data?.data || [];
          
          // Merge with any existing fields already saved in clientConfig for this collection
          const existingFields = currentConfig && Array.isArray(currentConfig.value) ? currentConfig.value : [];
          existingFields.forEach((ef) => {
            const efName = typeof ef === "object" ? ef.name || ef.field : ef;
            if (efName && !fields.some((f) => (f.name || f.field) === efName)) {
              fields.push(typeof ef === "object" ? ef : { name: efName, type: "string" });
            }
          });

          setFieldsList(fields);
        } catch (error) {
          console.error("Failed to fetch collection fields", error);
          const existingFields = currentConfig && Array.isArray(currentConfig.value) ? currentConfig.value : [];
          if (existingFields.length > 0) {
            setFieldsList(existingFields.map(ef => typeof ef === "object" ? ef : { name: ef, type: "string" }));
          }
        }
        setLoadingFields(false);
      };
      fetchFields();
    } else {
      setFieldsList([]);
    }
  }, [selectedCollection]);

  const handleCollectionChange = (event) => {
    setSelectedCollection(event.target.value);
  };

  const handleFieldsChange = (event) => {
    const { target: { value } } = event;
    const newFieldsArray = typeof value === 'string' ? value.split(',') : value;

    // Map names to full field objects with types and enums
    const mappedFieldsArray = newFieldsArray.map(fieldName => {
      const fullField = fieldsList.find(f => f.name === fieldName);
      if (fullField) {
        return {
          name: fullField.name,
          type: fullField.type,
          label: fullField.label || fullField.name,
          options: fullField.options || fullField.enum || undefined
        };
      }

      const existing = currentConfig?.value?.find(f => typeof f === 'object' ? (f.name === fieldName || f.field === fieldName) : f === fieldName);
      return typeof existing === 'object' ? existing : { name: fieldName, type: 'string' };
    });

    const newCategoryFields = [...categoryFields];
    const index = newCategoryFields.findIndex(f => f.key === selectedCollection);

    if (index >= 0) {
      if (mappedFieldsArray.length === 0) {
        newCategoryFields.splice(index, 1);
      } else {
        newCategoryFields[index] = { ...newCategoryFields[index], value: mappedFieldsArray };
      }
    } else if (mappedFieldsArray.length > 0) {
      newCategoryFields.push({ key: selectedCollection, value: mappedFieldsArray, isStatic: false, type: "array" });
    }

    if (onCategoryUpdate) {
      onCategoryUpdate(newCategoryFields);
    }
  };

  const handleDeleteCollection = (collectionKey) => {
    const newCategoryFields = [...categoryFields];
    let deletedConfigIndex = newCategoryFields.findIndex(f => f.key === '_deletedCollections');

    if (deletedConfigIndex === -1) {
      newCategoryFields.push({ key: '_deletedCollections', value: [], type: 'array', isStatic: false });
      deletedConfigIndex = newCategoryFields.length - 1;
    }

    const deletedArr = [...(newCategoryFields[deletedConfigIndex].value || [])];
    if (!deletedArr.includes(collectionKey)) {
      deletedArr.push(collectionKey);
      newCategoryFields[deletedConfigIndex] = { ...newCategoryFields[deletedConfigIndex], value: deletedArr };
    }

    if (onCategoryUpdate) {
      onCategoryUpdate(newCategoryFields);
    }
  };

  const handleRestoreCollection = (collectionKey) => {
    const newCategoryFields = [...categoryFields];
    const deletedConfigIndex = newCategoryFields.findIndex(f => f.key === '_deletedCollections');

    if (deletedConfigIndex !== -1) {
      const deletedArr = (newCategoryFields[deletedConfigIndex].value || []).filter(k => k !== collectionKey);
      newCategoryFields[deletedConfigIndex] = { ...newCategoryFields[deletedConfigIndex], value: deletedArr };

      if (onCategoryUpdate) {
        onCategoryUpdate(newCategoryFields);
      }
    }
  };

  return (
    <Box style={{ padding: 24, flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Dynamic Filters & Placeholders
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Configure collection fields for external filters and placeholders used in marketing workflows.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <FormControl variant="outlined" margin="dense" fullWidth style={{ marginBottom: 24 }}>
            <InputLabel id="collection-select-label">Target Collection</InputLabel>
            <Select
              labelId="collection-select-label"
              id="collection-select"
              value={selectedCollection}
              onChange={handleCollectionChange}
              disabled={loadingCollections}
              label="Target Collection"
              onClose={() => setCollectionSearch('')}
            >
              <ListSubheader style={{ paddingTop: 8, paddingBottom: 8, backgroundColor: '#fff', zIndex: 2 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search collections..."
                  fullWidth
                  variant="outlined"
                  value={collectionSearch}
                  onChange={(e) => setCollectionSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              {loadingCollections && <MenuItem disabled><CircularProgress size={20} style={{ marginRight: 8 }} /> Loading...</MenuItem>}
              {!loadingCollections && collections.filter(name => name.toLowerCase().includes(collectionSearch.toLowerCase())).map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
              {!loadingCollections && collections.filter(name => name.toLowerCase().includes(collectionSearch.toLowerCase())).length === 0 && (
                <MenuItem disabled>No collections found</MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl variant="outlined" margin="dense" fullWidth disabled={!selectedCollection || loadingFields}>
            <InputLabel id="fields-mutiple-checkbox-label">Filter Fields</InputLabel>
            <Select
              labelId="fields-mutiple-checkbox-label"
              id="fields-mutiple-checkbox"
              multiple
              value={selectedFields}
              onChange={handleFieldsChange}
              label="Filter Fields"
              renderValue={(selected) => {
                if (selected.length === 0) return <em>No fields selected</em>;
                if (selected.length <= 3) return selected.join(', ');
                return `${selected.length} fields selected`;
              }}
              MenuProps={MenuProps}
              onClose={() => setFieldSearch('')}
            >
              <ListSubheader style={{ paddingTop: 8, paddingBottom: 8, backgroundColor: '#fff', zIndex: 2 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Search fields..."
                  fullWidth
                  variant="outlined"
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListSubheader>
              {loadingFields && <MenuItem disabled><CircularProgress size={20} style={{ marginRight: 8 }} /> Loading...</MenuItem>}
              {!loadingFields && fieldsList.filter(field => field.name.toLowerCase().includes(fieldSearch.toLowerCase())).map((field) => (
                <MenuItem key={field.name} value={field.name} style={{ paddingTop: 4, paddingBottom: 4 }}>
                  <Checkbox checked={selectedFields.indexOf(field.name) > -1} color="primary" size="small" style={{ padding: 4 }} />
                  <ListItemText primary={field.name} secondary={field.type} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                </MenuItem>
              ))}
              {!loadingFields && fieldsList.filter(field => field.name.toLowerCase().includes(fieldSearch.toLowerCase())).length === 0 && (
                <MenuItem disabled>No fields found</MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Currently Configured Fields
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">Show deleted collections</Typography>}
            />
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="configured fields table">
              <TableHead style={{ backgroundColor: '#f9fafb' }}>
                <TableRow>
                  <TableCell><strong>Collection Name</strong></TableCell>
                  <TableCell><strong>Fields</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const deletedConfig = categoryFields.find(f => f.key === '_deletedCollections');
                  const deletedKeys = deletedConfig ? (deletedConfig.value || []) : [];

                  const visibleConfigs = categoryFields.filter(c => {
                    if (c.key === '_deletedCollections' || c.key === 'allowedExternalCollections') return false; // Never show metadata tracker collections
                    const isDel = deletedKeys.includes(c.key);
                    return showDeleted ? true : !isDel;
                  });

                  if (!visibleConfigs || visibleConfigs.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="textSecondary" style={{ padding: 16 }}>
                            No collections available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return visibleConfigs.map((config) => {
                    const isDeleted = deletedKeys.includes(config.key);
                    return (
                      <TableRow key={config.key} style={{ opacity: isDeleted ? 0.6 : 1, backgroundColor: isDeleted ? '#f5f5f5' : 'inherit' }}>
                        <TableCell>
                          <strong>{config.key}</strong>
                          {isDeleted && <Typography variant="caption" color="error" display="block">(Deleted)</Typography>}
                        </TableCell>
                        <TableCell>
                          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {Array.isArray(config.value) && config.value.map(f => {
                              const label = typeof f === 'object' ? f.name || f.field : f;
                              const hasOptions = typeof f === 'object' && Array.isArray(f.options || f.enum) && (f.options || f.enum).length > 0;
                              const isObject = typeof f === 'object' && (f.type === 'object' || f.type === 'mixed' || f.type === 'map' || f.name === 'extensions' || f.field === 'extensions');

                              let chipStyle = {};
                              let chipVariant = "outlined";
                              if (hasOptions) {
                                chipStyle = { backgroundColor: '#0d3559', color: '#fff', border: 'none' };
                                chipVariant = "default";
                              } else if (isObject) {
                                chipStyle = { backgroundColor: '#f57c00', color: '#fff', border: 'none' };
                                chipVariant = "default";
                              }

                              return <Chip key={label} label={label} size="small" variant={chipVariant} onClick={() => !isDeleted && handleChipClick(config.key, f)} style={{ cursor: isDeleted ? 'default' : 'pointer', ...chipStyle }} />;
                            })}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {isDeleted ? (
                            <Button size="small" style={{ color: '#0d3559' }} onClick={() => handleRestoreCollection(config.key)}>Restore</Button>
                          ) : (
                            <Button size="small" style={{ color: '#d32f2f' }} onClick={() => handleDeleteCollection(config.key)}>Delete</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Allowed External Collections Section */}
      <Paper
        variant="outlined"
        style={{
          padding: 20,
          marginTop: 32,
          backgroundColor: '#fafbfc',
          borderRadius: 8,
          borderColor: '#e2e8f0',
          borderLeft: '4px solid #0d3559',
        }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d3559', marginBottom: 4 }}>
          Allowed External Collections
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
          Specify database collection names (e.g. <code style={{ color: '#0d3559', backgroundColor: 'rgba(13, 53, 89, 0.08)', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>Candidate</code>, <code style={{ color: '#0d3559', backgroundColor: 'rgba(13, 53, 89, 0.08)', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>Company</code>) allowed for dynamic filtering and targeting in marketing workflows.
        </Typography>

        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginBottom: 14 }}>
          {allowedExternalCollections.map((colName, idx) => (
            <Chip
              key={idx}
              label={colName}
              onDelete={() => handleRemoveExternalCollection(idx)}
              variant="outlined"
              size="small"
              style={{
                fontWeight: 600,
                fontSize: '0.8rem',
                backgroundColor: 'rgba(13, 53, 89, 0.08)',
                borderColor: '#0d3559',
                color: '#0d3559',
              }}
            />
          ))}
          {allowedExternalCollections.length === 0 && (
            <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
              No external collections added yet.
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 10, maxWidth: 520 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Type collection name (e.g. Candidate) and press Enter"
            value={newCollectionInput}
            onChange={(e) => setNewCollectionInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddExternalCollection();
              }
            }}
            fullWidth
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddExternalCollection}
            disabled={!newCollectionInput.trim()}
            style={{
              backgroundColor: newCollectionInput.trim() ? '#0d3559' : '#e0e0e0',
              color: newCollectionInput.trim() ? '#ffffff' : '#9e9e9e',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              padding: '7px 20px',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            Add
          </Button>
        </Box>
      </Paper>
      <Dialog open={optionsDialogOpen} onClose={() => setOptionsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" flexDirection="column">
            <Typography variant="h6">Custom Dropdown Options</Typography>
            {editingField.fieldObj && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                Selected Field: <strong>{typeof editingField.fieldObj === 'object' ? editingField.fieldObj.name || editingField.fieldObj.field : editingField.fieldObj}</strong>
                {typeof editingField.fieldObj === 'object' && editingField.fieldObj.type && (
                  <span> | Type: <span style={{ textTransform: 'capitalize' }}>{editingField.fieldObj.type}</span></span>
                )}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingField.fieldObj &&
            (typeof editingField.fieldObj === 'object' &&
              (editingField.fieldObj.type === 'object' || editingField.fieldObj.type === 'mixed' || editingField.fieldObj.type === 'map' || editingField.fieldObj.name === 'extensions')) ? (
            <Box mt={1} p={2} border={1} borderColor="grey.300" borderRadius={4}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Nested Fields</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  style={{ color: '#0d3559', borderColor: '#0d3559' }}
                  onClick={handleFetchNestedSchema}
                  disabled={loadingNestedSchema}
                >
                  {loadingNestedSchema ? <CircularProgress size={16} /> : "Fetch Nested Schema"}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Select nested fields for sub-menus.
              </Typography>

              {rawNestedSchema && (
                <Box maxHeight={350} overflow="auto" mt={1}>
                  <Box display="flex" flexDirection="column" border="1px solid #f0f0f0" borderRadius={4} p={1}>
                    {Object.entries(rawNestedSchema).map(([childName, childType]) => {
                      const rootName = typeof editingField.fieldObj === 'object' ? editingField.fieldObj.name || editingField.fieldObj.field : editingField.fieldObj;
                      return (
                        <SchemaNode
                          key={childName}
                          name={childName}
                          type={childType}
                          path={`${rootName}.${childName}`}
                          selectedPaths={selectedNestedPaths}
                          onToggle={(path, checked) => {
                            if (checked) {
                              setSelectedNestedPaths(prev => [...prev, path]);
                            } else {
                              setSelectedNestedPaths(prev => prev.filter(p => p !== path));
                            }
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <>
              {(editingField?.fieldObj?.enum && Array.isArray(editingField.fieldObj.enum) && editingField.fieldObj.enum.length > 0) || (editingField?.fieldObj?.options && Array.isArray(editingField.fieldObj.options) && editingField.fieldObj.options.length > 0) ? (
                <>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    This field has predefined options from its schema that cannot be modified.
                  </Typography>
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {(editingField.fieldObj.enum || editingField.fieldObj.options).map(opt => (
                      <Chip key={opt} label={opt} size="small" variant="outlined" style={{ borderColor: '#0d3559', color: '#0d3559' }} />
                    ))}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Define custom dropdown options for this field.
                  </Typography>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Options (Comma separated)"
                    fullWidth
                    variant="outlined"
                    value={customOptionsText}
                    onChange={(e) => setCustomOptionsText(e.target.value)}
                    placeholder="e.g. Option1, Option2"
                  />
                </>
              )}
            </>
          )}

          <Box mt={4}>
            <Typography variant="subtitle2" gutterBottom>Quick Filter Templates</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Define 1-click filter buttons.
            </Typography>

            {quickFilters.map((qf, index) => (
              <Box key={index} display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
                <Chip label={qf.label} style={{ color: '#0d3559', borderColor: '#0d3559' }} variant="outlined" size="small" />
                <Typography variant="caption">
                  {qf.nestedField ? `[${qf.nestedField.split('.').pop()}] ` : ''}{qf.operator} {qf.value}
                </Typography>
                <Button size="small" style={{ color: '#0d3559' }} onClick={() => {
                  setNewQuickFilter({ ...qf });
                  const newF = [...quickFilters];
                  newF.splice(index, 1);
                  setQuickFilters(newF);
                }}>Edit</Button>
                <Button size="small" style={{ color: '#d32f2f' }} onClick={() => {
                  const newF = [...quickFilters];
                  newF.splice(index, 1);
                  setQuickFilters(newF);
                }}>Remove</Button>
              </Box>
            ))}

            <Box display="flex" alignItems="center" mt={2} style={{ gap: 8, flexWrap: 'wrap' }}>
              <TextField
                size="small" variant="outlined" label="Button Label" placeholder="e.g. Past Bookings"
                value={newQuickFilter.label} onChange={(e) => setNewQuickFilter({ ...newQuickFilter, label: e.target.value })}
              />

              {editingField.fieldObj &&
                (typeof editingField.fieldObj === 'object' &&
                  (editingField.fieldObj.type === 'object' || editingField.fieldObj.type === 'mixed' || editingField.fieldObj.type === 'map' || editingField.fieldObj.name === 'extensions')) && selectedNestedPaths.length > 0 && (
                  <FormControl variant="outlined" size="small" style={{ width: 160 }}>
                    <InputLabel>Target Field</InputLabel>
                    <Select
                      label="Target Field"
                      value={newQuickFilter.nestedField || ''}
                      onChange={(e) => {
                        const sel = e.target.value;
                        let defaultOp = 'equals';
                        if (sel && flattenedSchema) {
                          const flatField = flattenedSchema.find(f => f.path === sel);
                          const fType = String(flatField ? flatField.type : 'string').toLowerCase();
                          if (fType === 'boolean' || fType === 'bool') defaultOp = 'is';
                          else if (fType.includes('date') || fType.includes('time') || sel.toLowerCase().includes('date')) defaultOp = 'on';
                          else if (fType === 'array' || fType === 'multiselect') defaultOp = 'hasAny';

                          setNewQuickFilter({ ...newQuickFilter, nestedField: sel, operator: defaultOp, fieldType: flatField ? flatField.type : 'string' });
                        } else {
                          const fType = String(editingField.fieldObj.type || 'string').toLowerCase();
                          if (fType === 'boolean' || fType === 'bool') defaultOp = 'is';
                          else if (fType.includes('date') || fType.includes('time') || String(editingField.fieldObj.name || '').toLowerCase().includes('date')) defaultOp = 'on';
                          else if (fType === 'array' || fType === 'multiselect') defaultOp = 'hasAny';

                          setNewQuickFilter({ ...newQuickFilter, nestedField: sel, operator: defaultOp, fieldType: editingField.fieldObj.type || 'string' });
                        }
                      }}
                    >
                      <MenuItem value=""><em>(Root object)</em></MenuItem>
                      {selectedNestedPaths.map(path => {
                        const rootName = typeof editingField.fieldObj === 'object' ? editingField.fieldObj.name || editingField.fieldObj.field : editingField.fieldObj;
                        const rel = path.startsWith(rootName + '.') ? path.substring(rootName.length + 1) : path;
                        return <MenuItem key={path} value={path}>{rel}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                )}

              <FormControl variant="outlined" size="small" style={{ width: 130 }}>
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={newQuickFilter.operator} onChange={(e) => setNewQuickFilter({ ...newQuickFilter, operator: e.target.value })}
                >
                  {getOperatorsForCurrentField().map(op => (
                    <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* <TextField 
                size="small" variant="outlined" label="Value (Optional)" placeholder="e.g. {{TODAY}} or leave blank"
                value={newQuickFilter.value} onChange={(e) => setNewQuickFilter({...newQuickFilter, value: e.target.value})}
              /> */}
              <Button variant="outlined" size="small" style={{ height: 40, color: '#0d3559', borderColor: '#0d3559' }} onClick={() => {
                if (newQuickFilter.label && newQuickFilter.operator) {
                  setQuickFilters([...quickFilters, { ...newQuickFilter }]);
                  setNewQuickFilter({ label: '', operator: 'equals', value: '', nestedField: '', fieldType: 'string' });
                }
              }}>ADD</Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button onClick={handleSaveOptions} style={{ backgroundColor: '#0d3559', color: '#fff' }} variant="contained">
            Save Config
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicFiltersConfig;
